import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EVENTS } from '../../constants';
import { useCurrentUser } from '../../context/UserContext';
import { EventPropTypes } from '../../utils/useEvent';
import useUser from '../../utils/useUser';
import EditButton from '../Controls/EditButton';
import UserAvatar from '../GroupView/UserAvatar';
import RSVPYesIcon from '../Icons/RSVPYes';
import AddToCalendar from './AddToCalendar';
import EventRSVPStatus from './EventRSVPStatus';

const styles = {
  cardHeader: {
    paddingTop: '12px',
    paddingRight: '12px',
    paddingBottom: 0,
  },
  cardAction: {
    marginTop: 0,
    marginRight: 0,
  },
  cardTitle: {
    marginLeft: '10px',
  },
  eventDetail: {
    margin: '20px 10px',
  },
  icon: {
    fill: 'none',
    stroke: (theme) => theme.palette.primary.main,
  },
  addToCalendar: {
    marginTop: 0,
  },
}

export default function EventDetails({ event, editable }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useCurrentUser();

  const owner = event?.users.find((u) => u.role === EVENTS.ROLE.OWNER);
  const { data: ownerData } = useUser(owner?.id);

  const ownerName = `${ownerData?.firstName} ${ownerData?.lastName}`;
  const startTime = dayjs(event.startTime);
  const endTime = dayjs(event.endTime);
  const attendees = event.users.filter((u) => u.status === EVENTS.STATUS.YES);

  return (
    <Container>
      <Card sx={styles.card}>
        <CardHeader
          styles={{
            root: styles.cardHeader,
            action: styles.cardAction,
          }}
          action={
            editable && (
              <EditButton
                onClick={() => navigate(`/events/update/${event._id}`)}
              />
            )
          }
        />
        <CardContent>
          <Typography
            color="primary"
            variant="h2"
            sx={styles.cardTitle}
          >
            {event.title}
          </Typography>
          <Box
            display="flex"
            alignItems="flex-start"
            sx={styles.eventDetail}
          >
            <CalendarTodayOutlinedIcon color="primary" />
            <Box
              display="flex"
              flexDirection="column"
              style={{ marginLeft: '20px' }}
            >
              <Typography color="primary" variant="h5">
                {startTime.format('dddd, MMM D, YYYY')}
              </Typography>
              <Typography color="primary" variant="h5">
                {startTime.format('h:mm A')} - {endTime.format('h:mm A')}
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="flex-start"
            sx={styles.eventDetail}
          >
            <RoomOutlinedIcon color="primary" />
            <Box style={{ marginLeft: '20px' }}>
              <Typography color="primary" variant="h5">
                {event.location}
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="flex-start"
            sx={styles.eventDetail}
            onClick={() => navigate(`${location.pathname}/attendees`)}
          >
            <RSVPYesIcon sx={styles.icon} />
            <Box
              display="flex"
              flexDirection="column"
              style={{ marginLeft: '20px' }}
            >
              <Typography color="primary" variant="h5">
                {attendees.length === 1
                  ? '1 Person'
                  : `${attendees.length} People`}{' '}
                Going
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            sx={styles.eventDetail}
          >
            <EventRSVPStatus event={event} userId={user?._id} />
          </Box>
          <Card style={{ margin: '0 5px' }}>
            <CardHeader
              avatar={
                <UserAvatar aria-label="user-avatar" user={owner} small />
              }
              title={
                <Typography color="primary" variant="h5">
                  {ownerName}
                </Typography>
              }
            ></CardHeader>
            <CardContent style={{ paddingTop: 0 }}>
              <Typography variant="body2">{event.description}</Typography>
            </CardContent>
          </Card>
        </CardContent>
        <Container sx={styles.addToCalendar}>
          <AddToCalendar eventId={event._id} type="secondary" />
        </Container>
      </Card>
    </Container>
  );
}

EventDetails.propTypes = {
  event: EventPropTypes.isRequired,
  editable: PropTypes.bool,
};
